import { useRef } from "react";
import ColumnResizeHandle from "./ColumnResizeHandle";
import { ArrowDown, ArrowUp } from "lucide-react";

// Shared clickable/sortable <th>, used by both JobsTable and DataTable so the
// two tables' sort affordance can't drift. `onSort` is omitted (not merely a
// no-op) for a column with no `value(row)` — nothing to sort by — so it
// renders as a plain, non-interactive header exactly like before this existed.
//
// Resize (width/resizable/onResize/onResizeEnd) is orthogonal to sort — a
// column can be resizable whether or not it's sortable — so the handle is
// added to both branches independently rather than folded into the sort one.
export default function SortableColumnHeader({
  col,
  sort,
  onSort,
  className,
  width,
  resizable,
  onResize,
  onResizeEnd,
}) {
  // Whole-cell hover fill (owner ask) — light orange (brand-50, the same
  // tint this app already uses for an "active" row elsewhere), not just a
  // text-colour change, now that the resize handles are full-height bars
  // that read as real borders between columns: the fill has something to
  // stop cleanly at instead of bleeding into the next header.
  // Every header cell paints its own background (owner review, Oct 27 2026):
  // a transparent, positioned <th> could show the white table behind it
  // instead of the row's grey after a repaint (one header went white after a
  // click). Hover is a neutral step darker, and only on a sortable header —
  // the old brand-blue tint on every header read as a stuck highlight.
  const headerCls = `${className} relative min-w-0 truncate bg-surface-muted transition-colors${resizable ? " pr-3" : ""}`;

  // ⚠️⚠️⚠️ EXPLICIT `max-width` on the <th> ITSELF, not just `width` — and
  // not just on the <colgroup>'s <col>. Confirmed by direct DOM-level
  // testing in a live browser session (not a guess): under
  // `table-layout: fixed`, Chromium still lets a cell's/column's rendered
  // width sit at whichever is LARGER of the assigned `<col>` width and the
  // longest content across every row in that column — `width` (on the col,
  // the <td>, or the <th>) does NOT override that floor, `min-width: 0`
  // does NOT override it either. `max-width` is the one property that
  // actually forces the cell (and, cascading, its column) narrower than
  // its content. This is the real fix behind "dragging a column wider
  // works, narrower doesn't go below its longest value" — a `<colgroup>`
  // width alone was never going to be enough, in either direction, once a
  // real drag needs to go BELOW existing content.
  const headerStyle = resizable ? { width, maxWidth: width } : undefined;

  // ⚠️⚠️ A resize gesture and a sort click both end on the SAME <th> — the
  // handle's own stopPropagation should stop the click from ever reaching
  // here, but pointer-capture + a derived compatibility click event is a
  // known cross-browser rough edge (owner-reported: dragging the handle was
  // also triggering a sort). This ref is a second, unconditional guard:
  // `onResizeEnd` fires the instant a drag commits, one tick before the
  // browser's own click for that same gesture would fire, so setting it
  // there and clearing it on the very next click — whether or not that
  // click turns out to be the resize's own — reliably swallows exactly one
  // click and no more.
  const suppressNextSortRef = useRef(false);
  const handle = resizable && (
    <ColumnResizeHandle
      startWidth={width}
      onResize={(px) => onResize(col.key, px)}
      onCommit={(px) => {
        suppressNextSortRef.current = true;
        onResizeEnd(col.key, px);
      }}
    />
  );

  if (!onSort) {
    return (
      <th className={headerCls} style={headerStyle} title={col.label}>
        {col.label}
        {handle}
      </th>
    );
  }
  const active = sort?.key === col.key;
  const direction = active ? sort.direction : null;
  return (
    <th
      className={`${headerCls} cursor-pointer select-none hover:bg-surface-inset hover:text-ink`}
      style={headerStyle}
      onClick={() => {
        if (suppressNextSortRef.current) {
          suppressNextSortRef.current = false;
          return;
        }
        onSort(col.key);
      }}
      aria-sort={active ? (direction === "desc" ? "descending" : "ascending") : "none"}
      title={col.label}
    >
      <span className="inline-flex items-center gap-1">
        {col.label}
        {active && (
          <span className="text-brand-700" aria-hidden="true">{direction === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}</span>
        )}
      </span>
      {handle}
    </th>
  );
}
